import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentOrgId } from "@/hooks/auth";
import { queryConfigs } from "@/lib/queryClient";

/**
 * A org já produziu alguma reunião auto-vinculada a MAIS DE UM negócio
 * (`activities.custom_data.matched_deals`)?
 *
 * Existe só para gatear a busca por containment jsonb em `useActivitiesByDeal`.
 * Essa busca custava **27% do tempo total do banco** (570k chamadas, ~1,1s de
 * média) para servir **41 linhas em 1 org de 159** — sob generic plan o planner
 * monta um BitmapAnd que constrói o bitmap de org_id (163k linhas) só para
 * intersectar com o do GIN e descartar. Ver auditoria de performance 31/07.
 *
 * A flag é **auto-mantida** pelo trigger `trg_mark_org_has_multi_deal_meetings`
 * em `activities`: qualquer caminho de escrita que grave `matched_deals` liga a
 * flag da org na hora. Ou seja, não há passo manual e nenhuma org nova fica com
 * a reunião secundária escondida — no máximo ela aparece a partir da próxima
 * revalidação desta query.
 *
 * Cacheado como dado estrutural (30 min): é uma característica da org, não muda
 * durante o uso.
 */
export function useOrgHasMultiDealMeetings() {
  const { orgId } = useCurrentOrgId();

  return useQuery({
    queryKey: ["org-has-multi-deal-meetings", orgId],
    queryFn: async () => {
      if (!orgId) return false;
      const { data, error } = await supabase
        .from("organizations")
        .select("has_multi_deal_meetings")
        .eq("id", orgId)
        .single();

      // Fail-closed. Antes caía para `true` ("não esconder reunião"), mas isso
      // significa que uma falha transitória na leitura da flag LIGA a busca cara
      // em todas as orgs ao mesmo tempo — exatamente no momento em que o banco já
      // está degradado. Foi um dos fatores do incidente de 03/08. O custo de errar
      // para `false` é uma reunião secundária não aparecer até a próxima
      // revalidação; o custo de errar para `true` é derrubar o banco inteiro.
      if (error) return false;
      return Boolean(data?.has_multi_deal_meetings);
    },
    enabled: !!orgId,
    ...queryConfigs.structural,
  });
}
